import React, { useState, useEffect } from 'react';
import { getFirestore, collection, onSnapshot, query, orderBy, deleteDoc, doc, writeBatch } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Offcanvas, Button, Form, InputGroup, OverlayTrigger } from 'react-bootstrap';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';

import {
    FaTrashAlt,

} from 'react-icons/fa';
import './xDocList.css';

const COLORS = [
    '#b54d7ac9', 'rgba(50, 150, 170, 0.82)', '#63a346c3', '#a81d32cb',
    '#4062a0cb', '#2a7296c6', '#2141b5d2', '#6a6dbdd4', 'rgba(30, 140, 160, 0.85)',
    '#8e44adbf', '#27ae60bf', '#d35400bf', '#2c3e50bf', '#16a085bf', '#c0392bbf'
];

const XRSVPList = (props) => {
    const { isDark } = props;
    const [viewMode, setViewMode] = useState("list");
    const [rsvps, setRsvps] = useState([]);
    const [artists, setArtists] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const db = getFirestore();
    const auth = getAuth();

    const handleClose = () => { if (props.onToggle) props.onToggle(); setViewMode("list"); };
    const handleShow = () => { if (props.onToggle) props.onToggle(); };

    useEffect(() => {
        const unsubAuth = onAuthStateChanged(auth, (user) => {
            if (user) {
                localStorage.setItem("userEmail", user.email);
                localStorage.setItem("userName", user.displayName || "Member");
            }
        });
        const rsvpQuery = query(collection(db, "xRSVPs"), orderBy("createdAt", "desc"));
        const unsubRsvp = onSnapshot(rsvpQuery, (snap) => setRsvps(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const artistQuery = collection(db, "xArtistz");
        const unsubArtist = onSnapshot(artistQuery, (snap) => setArtists(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        return () => { unsubAuth(); unsubRsvp(); unsubArtist(); };
    }, [db, auth]);

    const filteredRsvps = rsvps.filter(r => {
        const artist = artists.find(a => a.id === r.artistId || a.title === r.artistName);
        return artist && artist.isActive === true && artist.status !== 'archived';
    });

    const chartData = Object.values(filteredRsvps.reduce((acc, rsvp) => {
        const category = rsvp.artistName || "Unknown Event";
        if (!acc[category]) acc[category] = { name: category, value: 0 };
        acc[category].value += 1;
        return acc;
    }, {}));

    const groupedRsvps = filteredRsvps
        .filter(r =>
            r.artistName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.eventDate?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .reduce((acc, rsvp) => {
            const group = rsvp.artistName || "Other Events";
            if (!acc[group]) acc[group] = [];
            acc[group].push(rsvp);
            return acc;
        }, {});

    const copyEmails = (emailList) => {
        const emails = emailList.map(r => r.userEmail).filter(e => e).join(", ");
        navigator.clipboard.writeText(emails);
        alert("Emails copied to clipboard!");
    };

    const clearActiveRSVPs = async () => {
        if (window.confirm(`Are you sure you want to delete ALL ${filteredRsvps.length} active RSVPs?`)) {
            const batch = writeBatch(db);
            filteredRsvps.forEach((r) => batch.delete(doc(db, "xRSVPs", r.id)));
            await batch.commit();
        }
    };

    const exportRSVPsToCSV = () => {
        if (filteredRsvps.length === 0) return alert("No RSVP data to export.");
        const headers = ["Artist", "Event Date", "Member Name", "Member Email", "RSVP Date"];
        const rows = filteredRsvps.map(r => [r.artistName, r.eventDate, r.userName, r.userEmail, r.createdAt ? new Date(r.createdAt.seconds * 1000).toLocaleDateString() : "N/A"]);
        const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `APC_RSVPs_${new Date().toLocaleDateString()}.csv`);
        link.click();
    };

    return (
        <>
          
                <Button onClick={handleShow} className="hoverX shadow-sm">
                    <span className="circle-btn centerCircle">👍🏼</span>RSVP
                </Button>
          
            <Offcanvas
                show={props.isOpen}
                onHide={handleClose}
                placement="end"
                className="bg-dark text-white"
                backdropClassName='adminBackdrop'

            >
                <Offcanvas.Header closeButton closeVariant="white" className="border-bottom border-secondary border-opacity-25">
                    <Offcanvas.Title >
                        <h3 className="modified xTitle animated rollIn ">
                            rsvp
                        </h3>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="p-3 d-flex flex-column" style={{ height: 'calc(100vh - 60px)' }}>
                    <div className="mb-4">
                        <InputGroup className="mb-2">
                            <Form.Control
                                placeholder="🔎 Search attendees or dates..."
                                className="glass-search text-white border-secondary shadow-none"
                                style={{ fontSize: '13px' }}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                        <div className="d-flex gap-2">
                            <Button variant={viewMode === "chart" ? "info" : "outline-info"} size="sm" className="fw-bold flex-grow-1" onClick={() => setViewMode(viewMode === "list" ? "chart" : "list")}>
                                {viewMode === "list" ? "📊 REPORT" : "📋 LIST"}
                            </Button>
                            <Button variant="outline-info" size="sm" className="fw-bold flex-grow-1" onClick={exportRSVPsToCSV}>EXPORT</Button>
                        </div>
                    </div>

                    <div className="flex-grow-1 overflow-auto pe-1">
                        <div className="d-flex justify-content-between align-items-center mb-4 bg-info bg-opacity-10 p-3 rounded border border-info border-opacity-25 shadow-sm">
                            <div>
                                <div className="smallest text-info fw-bold text-uppercase opacity-75" style={{ letterSpacing: '1px', fontSize: '10px' }}>Active Total</div>
                                <div className="d-flex align-items-baseline gap-1">
                                    <h2 className="m-0 fw-bold text-white">{filteredRsvps.length}</h2>
                                    <span className="text-white-50 fw-bold" style={{ fontSize: '12px', textTransform: 'uppercase' }}>Members</span>
                                </div>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                {filteredRsvps.length > 0 && (
                                    <Button variant="outline-danger" size="sm" className="smallest fw-bold py-1 px-2" onClick={clearActiveRSVPs}>CLEAR ALL</Button>
                                )}
                                {viewMode === "chart" && (
                                    <Button variant="link" className="text-info p-0 text-decoration-none" onClick={() => setViewMode("list")}>
                                        <i className="bi bi-x-circle fs-5"></i>
                                    </Button>
                                )}
                            </div>
                        </div>

                        {viewMode === "chart" ? (
                            <div className="py-2 text-center">
                                <h6 className="text-info smallest fw-bold text-uppercase mb-3">Active RSVP Distribution</h6>
                                <ResponsiveContainer width="100%" height={380}>
                                    <PieChart>
                                        <Pie data={chartData} cx="50%" cy="50%" labelLine={false} label={({ percent }) => `${(percent * 100).toFixed(0)}%`} outerRadius={110} dataKey="value">
                                            {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                                        </Pie>
                                        <Tooltip
                                            content={({ active, payload }) => {
                                                if (!active || !payload || !payload.length) return null;
                                                const data = payload[0];
                                                const percent = filteredRsvps.length > 0 ? ((data.value / filteredRsvps.length) * 100).toFixed(1) : 0;
                                                return (
                                                    <div style={{ backgroundColor: '#0d0d0df2', border: '1px solid #444', padding: '10px', borderRadius: '8px', minWidth: '160px', fontSize: '10px', textAlign: 'left' }}>
                                                        <div className="d-flex align-items-center gap-2 mb-2 pb-1 border-bottom border-secondary">
                                                            <span style={{ width: '10px', height: '10px', backgroundColor: data.fill, borderRadius: '3px', display: 'inline-block' }} />
                                                            <strong className="text-white font-11">{data.name}</strong>
                                                        </div>
                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <span className="text-white-50">Members:</span>
                                                            <strong className="text-light">{data.value}</strong>
                                                        </div>
                                                        <div className="d-flex justify-content-between align-items-center pt-1 border-top border-secondary border-opacity-25">
                                                            <span className="text-white-50">Share:</span>
                                                            <span className="badge bg-info text-dark fw-bold">{percent}%</span>
                                                        </div>
                                                    </div>
                                                );
                                            }}
                                        />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            Object.keys(groupedRsvps).map(artist => (
                                <div key={artist} className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2 border-bottom border-secondary border-opacity-25 pb-1">
                                        <div className="text-info fw-bold smallest text-uppercase">
                                            {artist} ({groupedRsvps[artist].length})
                                        </div>
                                        <Button
                                            variant="link"
                                            className="p-0 text-decoration-none text-info smallest fw-bold"
                                            style={{ fontSize: '9px' }}
                                            onClick={() => copyEmails(groupedRsvps[artist])}
                                        >
                                            COPY EMAILS
                                        </Button>
                                    </div>
                                    {groupedRsvps[artist].map(r => (
                                        <div key={r.id} className="doc-card p-3 mb-2 rounded border border-secondary bg-dark bg-opacity-50 shadow-sm">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div className="flex-grow-1 pe-2">
                                                    <div className="text-warning fw-bold mb-2" style={{ fontSize: '11px' }}>{r.eventDate}</div>
                                                    <div className="text-white-50 smallest">
                                                        <div className="mb-1"><span className="text-muted"></span> {r.userName}</div>
                                                        <div className="text-break "><svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 512 512" class="me-2 text-info" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M502.3 190.8c3.9-3.1 9.7-.2 9.7 4.7V400c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V195.6c0-5 5.7-7.8 9.7-4.7 22.4 17.4 52.1 39.5 154.1 113.6 21.1 15.4 56.7 47.8 92.2 47.6 35.7.3 72-32.8 92.3-47.6 102-74.1 131.6-96.3 154-113.7zM256 320c23.2.4 56.6-29.2 73.4-41.4 132.7-96.3 142.8-104.7 173.4-128.7 5.8-4.5 9.2-11.5 9.2-18.9v-19c0-26.5-21.5-48-48-48H48C21.5 64 0 85.5 0 112v19c0 7.4 3.4 14.3 9.2 18.9 30.6 23.9 40.7 32.4 173.4 128.7 16.8 12.2 50.2 41.8 73.4 41.4z"></path></svg>{r.userEmail}</div>
                                                    </div>
                                                </div>
                                                <div className="circle-btn centerCircle btn-delete d-flex align-items-center justify-content-center cursor-pointer mt-1" onClick={() => deleteDoc(doc(db, "xRSVPs", r.id))}>
                                                    <FaTrashAlt size={12} />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))
                        )}
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default XRSVPList;